import React from 'react'
import { UseFormRegisterReturn } from 'react-hook-form'

export interface ToggleOption {
  value: string
  label: string
  description?: string
  icon?: React.ReactNode
}

interface CallDirectionToggleProps {
  label?: string
  required?: boolean
  error?: string
  className?: string
  disabled?: boolean
  options: ToggleOption[]
  register?: UseFormRegisterReturn
  value?: string
  onChange?: (value: string) => void
  orientation?: 'horizontal' | 'vertical'
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  formValue?: string
}

export function CallDirectionToggle({
  label,
  required = false,
  error,
  className = '',
  disabled = false,
  options,
  register,
  value,
  onChange,
  orientation = 'horizontal',
  // size = 'lg',
  formValue,
}: CallDirectionToggleProps) {
  // Ensure we always have a value - prioritize value prop (from Controller), then formValue, then first option
  const currentValue = value ?? formValue ?? options[0]?.value

  const handleChange = (optionValue: string) => {
    // Don't change if it's already selected (prevent deselection)
    if (currentValue === optionValue) {
      return
    }

    if (register) {
      register.onChange({ target: { value: optionValue } })
    } else if (onChange) {
      onChange(optionValue)
    }
  }

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div
        className={`flex ${orientation === 'vertical' ? 'flex-col' : 'flex-row'} gap-2.5`}
      >
        {options.map(option => {
          const isSelected = currentValue === option.value
          return (
            <button
              key={option.value}
              type="button"
              disabled={disabled}
              onClick={() => handleChange(option.value)}
              className={`
                w-[308px] 
                border 
                bg-transparent 
                rounded-md
                p-[16px]
                flex items-start justify-items-start
                text-sm font-medium
                transition-colors duration-200
                hover:border-[#254769]
                focus:outline-none focus:ring-1 focus:ring-[#254769] focus:ring-opacity-50
                disabled:opacity-50 disabled:cursor-not-allowed
                ${
                  isSelected
                    ? 'border-[#254769] bg-[#254769] bg-opacity-5'
                    : 'border-light-gray-20'
                }
              `}
            >
              {option.icon && <span className="mr-2">{option.icon}</span>}
              <div className="flex flex-col items-start gap-1 text-start">
                <span className="">{option.label}</span>
                {option.description && (
                  <span className="text-xs text-gray-500">
                    {option.description}
                  </span>
                )}
              </div>
            </button>
          )
        })}
      </div>

      {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
    </div>
  )
}
